import {
  BaseSyntheticEvent,
  FormHTMLAttributes,
  PropsWithChildren,
  useEffect,
  useRef,
} from 'react'
import {
  FormProvider,
  SubmitHandler,
  useForm,
  UseFormProps,
  UseFormReturn,
} from 'react-hook-form'
import { FieldValues } from 'react-hook-form/dist/types/fields'

export type FormContainerProps<T extends FieldValues = FieldValues> =
  PropsWithChildren<
    UseFormProps<T> & {
      onSuccess?: SubmitHandler<T>
      FormProps?: FormHTMLAttributes<HTMLFormElement>
      handleSubmit?: (e: BaseSyntheticEvent<T>) => Promise<void> | void
      formContext?: UseFormReturn<T>
    }
  >

export function KistillFormContainer<
  TFieldValues extends FieldValues = FieldValues,
>({
  handleSubmit,
  children,
  FormProps,
  formContext,
  onSuccess,
  ...useFormProps
}: PropsWithChildren<FormContainerProps<TFieldValues>>) {
  const formRef = useRef<HTMLFormElement>(null)

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 's') {
        event.preventDefault()
        // Get the submit button and click it
        const submitButton = formRef.current?.querySelector(
          'button[type="submit"]',
        )
        if (submitButton) {
          ;(submitButton as HTMLButtonElement).click()
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  if (!formContext) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const methods = useForm<TFieldValues>({
      ...useFormProps,
    })
    const { handleSubmit } = methods

    return (
      <FormProvider {...methods}>
        <form
          onSubmit={handleSubmit(
            onSuccess
              ? onSuccess
              : // eslint-disable-next-line no-console
                () => console.log("submit handler 'onSubmit' is missing"),
          )}
          noValidate
          ref={formRef}
          {...FormProps}
        >
          {children}
        </form>
      </FormProvider>
    )
  }
  if (typeof onSuccess === 'function' && typeof handleSubmit === 'function') {
    console.warn(
      'Property "onSuccess will be ignored because handleSubmit is provided"',
    )
  }
  const checkKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key === 'Enter')
      if (e.target instanceof HTMLTextAreaElement) {
        // Allow default behavior for Enter key in a textarea
        return
      } else {
        e.preventDefault()
      }
  }
  return (
    <FormProvider {...formContext}>
      <form
        noValidate
        {...FormProps}
        ref={formRef}
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        onSubmit={
          handleSubmit
            ? handleSubmit
            : onSuccess
              ? formContext.handleSubmit(onSuccess)
              : // eslint-disable-next-line no-console
                () => console.log('submit handler is missing')
        }
        onKeyDown={(e) => checkKeyDown(e)}
      >
        {children}
      </form>
    </FormProvider>
  )
}
